import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Tab, Tabs } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface Evidence {
  id: number;
  evidence_type: string;
  source_name: string;
  source_reference: string;
  source_url: string;
  is_authoritative: boolean;
  attachment: string | null;
  notes: string;
  created_at: string;
}

interface CorporateAction {
  id: number;
  action_type: string;
  status: string;
  company_name: string;
  symbol: string;
  source_isin: string;
  destination_isin: string;
  announcement_date: string | null;
  record_date: string | null;
  ex_date: string | null;
  effective_date: string;
  ratio_numerator: string;
  ratio_denominator: string;
  confidence: string;
  source_provider: string;
  provider_reference: string;
  notes: string;
  version: number;
  confirmation_phrase: string;
  release_one_supported: boolean;
  evidence: Evidence[];
  application_summary: Record<string, number>;
}

interface ActionApplication {
  id: number;
  username: string;
  broker_account_name: string;
  classification: string;
  status: string;
  eligible_quantity: string;
  before_source_quantity: string;
  after_source_quantity: string;
  before_destination_quantity: string;
  after_destination_quantity: string;
  fractional_quantity: string;
  verification_details: Record<string, string>;
  error_message: string;
  applied_at: string | null;
}

interface RollbackPreviewRow {
  application_id: number;
  username: string;
  broker_account_name: string;
  safe: boolean;
  blockers: string[];
}

interface RollbackPreview {
  scope: 'global' | 'selected';
  application_ids: number[];
  safe_count: number;
  blocked_count: number;
  confirmation_phrase: string;
  preview_token: string;
  rows: RollbackPreviewRow[];
}

interface ResolutionPreview {
  source_current: string;
  source_target: string;
  source_delta: string;
  destination_current: string;
  destination_target: string;
  destination_delta: string;
  confirmation_phrase: string;
  preview_token: string;
}

interface AuditEvent {
  id: number;
  event_type: string;
  actor_username: string | null;
  reason: string;
  created_at: string;
}

interface UserReport {
  id: number;
  application: number;
  username: string;
  company_name: string;
  action_type: string;
  broker_account_name: string;
  message: string;
  attachment: string | null;
  status: string;
  resolution_note: string;
  created_at: string;
}

interface Overview {
  actions: Record<string, number>;
  applications: Record<string, number>;
  open_user_reports: number;
  flags: {
    registry_enabled: boolean;
    portfolio_mutation_enabled: boolean;
    automatic_approval_enabled: boolean;
  };
}

interface StockOption {
  id: number;
  symbol: string;
  name: string;
  isin: string;
}

type ProtectedKind = 'approve' | 'rollback' | 'resolve' | null;

const statusColor = (status: string) => ({
  pending_review: 'warning',
  approved: 'primary',
  applying: 'primary',
  completed: 'success',
  applied_verified: 'success',
  completed_with_conflicts: 'danger',
  conflict: 'danger',
  failed: 'danger',
  rejected: 'secondary',
  unsupported_for_application: 'dark',
  awaiting_statement_verification: 'warning',
  rolled_back: 'secondary',
}[status] || 'secondary');

const words = (value: string) => value.replaceAll('_', ' ');

const SuperAdminCorporateActions: React.FC = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [actions, setActions] = useState<CorporateAction[]>([]);
  const [selected, setSelected] = useState<CorporateAction | null>(null);
  const [applications, setApplications] = useState<ActionApplication[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [reports, setReports] = useState<UserReport[]>([]);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [protectedKind, setProtectedKind] = useState<ProtectedKind>(null);
  const [protectedApplication, setProtectedApplication] = useState<ActionApplication | null>(null);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [targetSource, setTargetSource] = useState('');
  const [targetDestination, setTargetDestination] = useState('');
  const [rollbackPreview, setRollbackPreview] = useState<RollbackPreview | null>(null);
  const [resolutionPreview, setResolutionPreview] = useState<ResolutionPreview | null>(null);
  const [saving, setSaving] = useState(false);
  const [stockQuery, setStockQuery] = useState('');
  const [stockResults, setStockResults] = useState<StockOption[]>([]);
  const [stockSearching, setStockSearching] = useState(false);
  const [selectedStock, setSelectedStock] = useState<StockOption | null>(null);
  const [newAction, setNewAction] = useState({
    action_type: 'split',
    company_name: '',
    symbol: '',
    source_isin: '',
    destination_isin: '',
    effective_date: '',
    ex_date: '',
    ratio_numerator: '2',
    ratio_denominator: '1',
    confidence: 'medium',
    source_provider: 'manual',
    provider_reference: '',
    notes: '',
  });
  const [newEvidence, setNewEvidence] = useState({
    evidence_type: 'admin_attachment',
    source_name: '',
    source_reference: '',
    source_url: '',
    notes: '',
    is_authoritative: false,
  });
  const [newEvidenceFile, setNewEvidenceFile] = useState<File | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (submittedSearch) params.search = submittedSearch;
      const [overviewResponse, actionResponse, reportResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/super-admin/corporate-actions/overview/`),
        axios.get(`${API_BASE_URL}/super-admin/corporate-actions/`, { params }),
        axios.get(`${API_BASE_URL}/super-admin/corporate-actions/reports/`),
      ]);
      setOverview(overviewResponse.data);
      setActions(Array.isArray(actionResponse.data) ? actionResponse.data : []);
      setReports(Array.isArray(reportResponse.data) ? reportResponse.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to load the super-admin queue.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, submittedSearch]);

  const loadAction = async (actionId: number) => {
    setDetailLoading(true);
    setError('');
    try {
      const [detailResponse, applicationResponse, auditResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/super-admin/corporate-actions/${actionId}/`),
        axios.get(`${API_BASE_URL}/super-admin/corporate-actions/${actionId}/applications/`),
        axios.get(`${API_BASE_URL}/super-admin/corporate-actions/${actionId}/audit/`),
      ]);
      setSelected(detailResponse.data);
      setApplications(Array.isArray(applicationResponse.data) ? applicationResponse.data : []);
      setAudit(Array.isArray(auditResponse.data) ? auditResponse.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to load this action.');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_superuser) loadQueue();
  }, [user?.is_superuser, loadQueue]);

  if (!user?.is_superuser) {
    return <Alert variant="danger" className="mt-4">This workspace is available only to the StockHunt super admin.</Alert>;
  }

  const refreshSelected = async () => {
    await loadQueue();
    if (selected) await loadAction(selected.id);
  };

  const generatePreview = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/super-admin/corporate-actions/${selected.id}/preview/`, {});
      setNotice(`Preview refreshed for ${response.data.application_count} account result(s).`);
      await refreshSelected();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to generate the preview.');
    } finally {
      setSaving(false);
    }
  };

  const openProtected = async (kind: ProtectedKind, application?: ActionApplication) => {
    if (!selected || !kind) return;
    setReason('');
    setPassword('');
    setConfirmation('');
    setRollbackPreview(null);
    setResolutionPreview(null);
    setProtectedApplication(application || null);
    if (application) {
      setTargetSource(application.verification_details?.reported_source_quantity || application.after_source_quantity);
      setTargetDestination(application.verification_details?.reported_destination_quantity || application.after_destination_quantity);
    }
    if (kind === 'rollback') {
      setSaving(true);
      setError('');
      try {
        const response = await axios.post(
          `${API_BASE_URL}/super-admin/corporate-actions/${selected.id}/rollback-preview/`,
          application ? { application_ids: [application.id] } : {}
        );
        setRollbackPreview(response.data);
        setProtectedKind(kind);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Unable to generate the rollback dependency preview.');
      } finally {
        setSaving(false);
      }
      return;
    }
    setProtectedKind(kind);
  };

  const requiredPhrase = protectedKind === 'approve'
    ? selected?.confirmation_phrase || ''
    : protectedKind === 'rollback'
      ? rollbackPreview?.confirmation_phrase || ''
      : protectedKind === 'resolve'
        ? resolutionPreview?.confirmation_phrase || ''
        : '';

  const generateResolutionPreview = async () => {
    if (!protectedApplication || !targetSource) return;
    setSaving(true);
    setError('');
    setConfirmation('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/super-admin/corporate-action-applications/${protectedApplication.id}/resolution-preview/`,
        {
          target_source_quantity: targetSource,
          target_destination_quantity: targetDestination || undefined,
        }
      );
      setResolutionPreview(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.target_source_quantity?.[0] || 'Unable to preview this account correction.');
    } finally {
      setSaving(false);
    }
  };

  const submitProtected = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !protectedKind) return;
    setSaving(true);
    setError('');
    try {
      if (protectedKind === 'resolve' && protectedApplication) {
        if (!resolutionPreview) {
          setError('Generate and review the account correction preview first.');
          return;
        }
        await axios.post(
          `${API_BASE_URL}/super-admin/corporate-action-applications/${protectedApplication.id}/resolve/`,
          {
            reason,
            password,
            confirmation,
            target_source_quantity: targetSource,
            target_destination_quantity: targetDestination || undefined,
            preview_token: resolutionPreview.preview_token,
          }
        );
      } else if (protectedKind === 'rollback') {
        if (!rollbackPreview) {
          setError('Generate and review the rollback dependency preview first.');
          return;
        }
        await axios.post(
          `${API_BASE_URL}/super-admin/corporate-actions/${selected.id}/rollback/`,
          {
            reason,
            password,
            confirmation,
            preview_token: rollbackPreview.preview_token,
          }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/super-admin/corporate-actions/${selected.id}/${protectedKind}/`,
          { reason, password, confirmation }
        );
      }
      setNotice(
        protectedKind === 'approve'
          ? 'Approval recorded and a resumable account batch was created.'
          : protectedKind === 'rollback'
            ? 'Rollback request recorded and queued.'
            : 'This user account correction was recorded for statement re-verification.'
      );
      setProtectedKind(null);
      setRollbackPreview(null);
      setResolutionPreview(null);
      await refreshSelected();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(data?.password?.[0] || data?.confirmation?.[0] || data?.error || 'Protected operation failed.');
    } finally {
      setSaving(false);
    }
  };

  const rejectAction = async () => {
    if (!selected) return;
    const rejectionReason = window.prompt('Why should this action be rejected?');
    if (!rejectionReason || rejectionReason.trim().length < 3) return;
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/super-admin/corporate-actions/${selected.id}/reject/`, {
        reason: rejectionReason.trim(),
      });
      setNotice('Action rejected with an audit reason.');
      await refreshSelected();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to reject this action.');
    } finally {
      setSaving(false);
    }
  };

  const retryFailed = async () => {
    if (!selected) return;
    const retryReason = window.prompt('Why is it safe to retry the failed account results?');
    if (!retryReason || retryReason.trim().length < 3) return;
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/super-admin/corporate-actions/${selected.id}/retry/`, {
        reason: retryReason.trim(),
      });
      setNotice('Failed safe applications were queued for retry.');
      await refreshSelected();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'No safe failed application is ready to retry.');
    } finally {
      setSaving(false);
    }
  };

  const createAction = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...newAction,
        destination_isin: newAction.destination_isin.trim().toUpperCase(),
        source_isin: newAction.source_isin.trim().toUpperCase(),
        symbol: newAction.symbol.trim().toUpperCase(),
        ex_date: newAction.ex_date || null,
      };
      const response = await axios.post(`${API_BASE_URL}/super-admin/corporate-actions/`, payload);
      setShowCreate(false);
      setNotice('Manual candidate created. Add or verify evidence, then generate its account preview.');
      setStatusFilter('pending_review');
      await loadQueue();
      await loadAction(response.data.id);
    } catch (err: any) {
      const data = err?.response?.data;
      setError(data?.non_field_errors?.[0] || data?.source_isin?.[0] || data?.error || 'Unable to create the action.');
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    setStockQuery('');
    setStockResults([]);
    setSelectedStock(null);
    setNewAction((current) => ({
      ...current,
      company_name: '',
      symbol: '',
      source_isin: '',
      destination_isin: '',
    }));
    setShowCreate(true);
  };

  const searchStocks = async () => {
    const query = stockQuery.trim();
    if (query.length < 2) {
      setError('Enter at least two characters of the company, symbol, or ISIN.');
      return;
    }
    setStockSearching(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/stocks/`, {
        params: { search: query, page_size: 20 },
      });
      const results = Array.isArray(response.data?.results) ? response.data.results : [];
      setStockResults(results);
      if (results.length === 0) setError('No matching company was found in the stock master.');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to search the stock master.');
    } finally {
      setStockSearching(false);
    }
  };

  const chooseStock = (stock: StockOption) => {
    setSelectedStock(stock);
    setStockQuery(`${stock.symbol} - ${stock.name}`);
    setStockResults([]);
    setNewAction((current) => ({
      ...current,
      company_name: stock.name,
      symbol: stock.symbol,
      source_isin: stock.isin,
    }));
  };

  const addEvidence = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      Object.entries(newEvidence).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
      if (newEvidenceFile) formData.append('attachment', newEvidenceFile);
      await axios.post(
        `${API_BASE_URL}/super-admin/corporate-actions/${selected.id}/evidence/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setShowEvidence(false);
      setNewEvidence({
        evidence_type: 'admin_attachment', source_name: '', source_reference: '',
        source_url: '', notes: '', is_authoritative: false,
      });
      setNewEvidenceFile(null);
      await loadAction(selected.id);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to add evidence.');
    } finally {
      setSaving(false);
    }
  };

  const updateReport = async (report: UserReport, reportStatus: 'reviewing' | 'resolved' | 'dismissed') => {
    const note = reportStatus === 'reviewing'
      ? 'Investigation started.'
      : window.prompt(`Add the ${reportStatus} note:`);
    if (note === null || (reportStatus !== 'reviewing' && note.trim().length < 3)) return;
    try {
      await axios.patch(`${API_BASE_URL}/super-admin/corporate-actions/reports/${report.id}/`, {
        status: reportStatus,
        resolution_note: note,
      });
      await loadQueue();
    } catch (err: any) {
      setError(err?.response?.data?.resolution_note?.[0] || 'Unable to update the report.');
    }
  };

  return (
    <div className="superadmin-actions-page">
      <header className="superadmin-actions-hero">
        <div>
          <span className="page-eyebrow">Controlled operations</span>
          <h1>Corporate action command desk</h1>
          <p>One reviewed market event, separately preflighted for every user and broker account.</p>
        </div>
        <div className="superadmin-actions-hero__controls">
          <Badge bg={overview?.flags.portfolio_mutation_enabled ? 'danger' : 'warning'} text={overview?.flags.portfolio_mutation_enabled ? undefined : 'dark'}>
            {overview?.flags.portfolio_mutation_enabled ? 'Portfolio mutation ON' : 'Shadow mode: no portfolio mutation'}
          </Badge>
          <Button variant="light" onClick={openCreateModal}>+ Manual candidate</Button>
        </div>
      </header>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert variant="success" dismissible onClose={() => setNotice('')}>{notice}</Alert>}

      <section className="superadmin-metrics" aria-label="Corporate action queue summary">
        <article><span>Pending review</span><strong>{overview?.actions.pending_review || 0}</strong></article>
        <article><span>Account conflicts</span><strong>{overview?.applications.conflicts || 0}</strong></article>
        <article><span>Awaiting statement</span><strong>{overview?.applications.awaiting_statement || 0}</strong></article>
        <article><span>User reports</span><strong>{overview?.open_user_reports || 0}</strong></article>
      </section>

      <Tabs defaultActiveKey="queue" className="superadmin-tabs">
        <Tab eventKey="queue" title="Review queue">
          <div className="superadmin-workspace">
            <aside className="superadmin-queue">
              <Form onSubmit={(event) => { event.preventDefault(); setSubmittedSearch(search.trim()); }} className="superadmin-queue__filters">
                <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="pending_review">Pending review</option>
                  <option value="detected">Detected</option>
                  <option value="approved">Approved</option>
                  <option value="applying">Applying</option>
                  <option value="completed_with_conflicts">With conflicts</option>
                  <option value="completed">Completed</option>
                  <option value="unsupported_for_application">Preview only</option>
                  <option value="">All actions</option>
                </Form.Select>
                <div className="d-flex gap-2">
                  <Form.Control value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Symbol, ISIN, company" />
                  <Button type="submit" variant="dark">Find</Button>
                </div>
              </Form>
              {loading ? (
                <div className="superadmin-loading"><Spinner animation="border" /></div>
              ) : actions.length === 0 ? (
                <div className="superadmin-empty">No actions match this view.</div>
              ) : actions.map((action) => (
                <button
                  type="button"
                  key={action.id}
                  className={`superadmin-queue-card ${selected?.id === action.id ? 'is-selected' : ''}`}
                  onClick={() => loadAction(action.id)}
                >
                  <span className="superadmin-queue-card__type">{words(action.action_type)}</span>
                  <strong>{action.symbol || action.company_name}</strong>
                  <small>{action.company_name}</small>
                  <div>
                    <Badge bg={statusColor(action.status)}>{words(action.status)}</Badge>
                    <span>{action.ratio_numerator}:{action.ratio_denominator}</span>
                  </div>
                </button>
              ))}
            </aside>

            <main className="superadmin-detail">
              {!selected ? (
                <div className="superadmin-detail__blank">
                  <strong>Select an action to inspect its evidence and account impact.</strong>
                  <span>No decision controls appear until a candidate is selected.</span>
                </div>
              ) : detailLoading ? (
                <div className="superadmin-loading"><Spinner animation="border" /></div>
              ) : (
                <>
                  <div className="superadmin-detail__header">
                    <div>
                      <span className="page-eyebrow">Action #{selected.id} / version {selected.version}</span>
                      <h2>{selected.symbol || selected.company_name}</h2>
                      <p>{selected.company_name} / {words(selected.action_type)}</p>
                    </div>
                    <Badge bg={statusColor(selected.status)}>{words(selected.status)}</Badge>
                  </div>

                  <div className="superadmin-facts">
                    <div><span>Ratio</span><strong>{selected.ratio_numerator}:{selected.ratio_denominator}</strong></div>
                    <div><span>Effective</span><strong>{new Date(`${selected.effective_date}T00:00:00`).toLocaleDateString('en-IN')}</strong></div>
                    <div><span>Confidence</span><strong>{selected.confidence}</strong></div>
                    <div><span>Provider</span><strong>{selected.source_provider}</strong></div>
                    <div><span>Source ISIN</span><strong>{selected.source_isin}</strong></div>
                    <div><span>Destination</span><strong>{selected.destination_isin || 'Same security'}</strong></div>
                  </div>

                  <div className="superadmin-decision-bar">
                    <Button variant="outline-dark" onClick={generatePreview} disabled={saving || !selected.release_one_supported}>Refresh preview</Button>
                    <Button variant="success" onClick={() => openProtected('approve')} disabled={saving || !selected.release_one_supported || selected.status !== 'pending_review'}>Approve</Button>
                    <Button variant="outline-danger" onClick={rejectAction} disabled={saving || selected.status !== 'pending_review'}>Reject</Button>
                    <Button variant="outline-primary" onClick={retryFailed} disabled={!applications.some((application) => application.status === 'failed')}>Retry failed</Button>
                    <Button variant="danger" onClick={() => openProtected('rollback')} disabled={saving || !applications.some((application) => application.applied_at && application.status !== 'rolled_back')}>Rollback all</Button>
                  </div>

                  <section className="superadmin-section">
                    <div className="superadmin-section__heading">
                      <div><span className="page-eyebrow">Proof</span><h3>Evidence</h3></div>
                      <Button size="sm" variant="outline-primary" onClick={() => setShowEvidence(true)}>+ Evidence</Button>
                    </div>
                    <div className="superadmin-evidence-grid">
                      {selected.evidence.map((item) => (
                        <article key={item.id}>
                          <div><Badge bg={item.is_authoritative ? 'success' : 'secondary'}>{item.is_authoritative ? 'Authoritative' : words(item.evidence_type)}</Badge></div>
                          <strong>{item.source_name || 'Imported evidence'}</strong>
                          <span>{item.source_reference || item.notes || 'No reference supplied'}</span>
                          {item.source_url && <a href={item.source_url} target="_blank" rel="noreferrer">Open source</a>}
                          {item.attachment && <a href={item.attachment} target="_blank" rel="noreferrer">Open attachment</a>}
                        </article>
                      ))}
                    </div>
                  </section>

                  <section className="superadmin-section">
                    <div className="superadmin-section__heading">
                      <div><span className="page-eyebrow">Per-account preflight</span><h3>Applications ({applications.length})</h3></div>
                    </div>
                    <div className="superadmin-application-list">
                      {applications.length === 0 ? <p>Generate a preview to calculate affected accounts.</p> : applications.map((application) => (
                        <article key={application.id}>
                          <div className="superadmin-application-list__identity">
                            <strong>{application.username}</strong>
                            <span>{application.broker_account_name}</span>
                          </div>
                          <div><span>Eligible</span><strong>{Number(application.eligible_quantity).toLocaleString('en-IN')}</strong></div>
                          <div><span>Before / after</span><strong>{Number(application.before_source_quantity).toLocaleString('en-IN')} / {Number(application.after_source_quantity).toLocaleString('en-IN')}</strong></div>
                          <div><Badge bg={statusColor(application.status)}>{words(application.status)}</Badge><small>{words(application.classification)}</small></div>
                          <div className="d-flex gap-2 flex-wrap">
                            {application.status === 'conflict' && ['approved', 'applying', 'completed', 'completed_with_conflicts'].includes(selected.status) && (
                              <Button size="sm" variant="outline-danger" onClick={() => openProtected('resolve', application)}>Resolve user</Button>
                            )}
                            {application.applied_at && application.status !== 'rolled_back' && (
                              <Button size="sm" variant="outline-secondary" onClick={() => openProtected('rollback', application)}>Rollback user</Button>
                            )}
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>

                  <section className="superadmin-section">
                    <div className="superadmin-section__heading"><div><span className="page-eyebrow">Immutable trail</span><h3>Audit</h3></div></div>
                    <div className="superadmin-audit">
                      {audit.map((event) => (
                        <article key={event.id}>
                          <span>{new Date(event.created_at).toLocaleString('en-IN')}</span>
                          <strong>{words(event.event_type)}</strong>
                          <small>{event.actor_username || 'System'}{event.reason ? ` / ${event.reason}` : ''}</small>
                        </article>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </main>
          </div>
        </Tab>

        <Tab eventKey="reports" title={`User reports (${overview?.open_user_reports || 0})`}>
          <div className="superadmin-report-grid">
            {reports.length === 0 ? <div className="superadmin-empty">No user mismatch reports.</div> : reports.map((report) => (
              <Card key={report.id} className="superadmin-report-card">
                <Card.Body>
                  <div className="d-flex justify-content-between gap-2">
                    <div><span className="page-eyebrow">{words(report.action_type)}</span><h3>{report.company_name}</h3></div>
                    <Badge bg={report.status === 'resolved' ? 'success' : report.status === 'dismissed' ? 'secondary' : 'warning'} text={report.status === 'reviewing' || report.status === 'open' ? 'dark' : undefined}>{report.status}</Badge>
                  </div>
                  <p><strong>{report.username}</strong> / {report.broker_account_name}</p>
                  <blockquote>{report.message}</blockquote>
                  {report.resolution_note && <Alert variant="light" className="border">{report.resolution_note}</Alert>}
                  {report.attachment && <a href={report.attachment} target="_blank" rel="noreferrer">Open attachment</a>}
                  <div className="d-flex gap-2 mt-3">
                    <Button size="sm" variant="outline-primary" onClick={() => updateReport(report, 'reviewing')}>Mark reviewing</Button>
                    <Button size="sm" variant="success" onClick={() => updateReport(report, 'resolved')}>Resolve</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => updateReport(report, 'dismissed')}>Dismiss</Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </Tab>
      </Tabs>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg" centered>
        <Form onSubmit={createAction}>
          <Modal.Header closeButton><Modal.Title>Create manual candidate</Modal.Title></Modal.Header>
          <Modal.Body>
            <Alert variant="warning">This creates a review candidate, not a portfolio change. Approval still requires preview, password, typed confirmation, and audit reason.</Alert>
            <div className="row g-3">
              <Form.Group className="col-md-4"><Form.Label>Action type</Form.Label><Form.Select value={newAction.action_type} onChange={(event) => setNewAction({ ...newAction, action_type: event.target.value })}><option value="split">Stock split</option><option value="reverse_split">Reverse split</option><option value="bonus">Bonus</option><option value="isin_change">1:1 ISIN change</option><option value="merger">Merger (preview only)</option><option value="demerger">Demerger (preview only)</option></Form.Select></Form.Group>
              <Form.Group className="col-md-8 corporate-stock-search">
                <Form.Label>Find company</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    value={stockQuery}
                    onChange={(event) => { setStockQuery(event.target.value); setSelectedStock(null); }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') { event.preventDefault(); searchStocks(); }
                    }}
                    placeholder="Company, symbol, or ISIN"
                    aria-label="Find company"
                  />
                  <Button type="button" variant="outline-primary" onClick={searchStocks} disabled={stockSearching}>
                    {stockSearching ? <Spinner animation="border" size="sm" /> : 'Search'}
                  </Button>
                </div>
                {stockResults.length > 0 && <div className="corporate-stock-results" role="listbox" aria-label="Matching companies">
                  {stockResults.map((stock) => <button type="button" role="option" aria-selected={false} key={stock.id} onClick={() => chooseStock(stock)}>
                    <strong>{stock.symbol}</strong><span>{stock.name}</span><small>{stock.isin}</small>
                  </button>)}
                </div>}
              </Form.Group>
              <Form.Group className="col-md-4"><Form.Label>Symbol</Form.Label><Form.Control aria-label="Symbol" readOnly value={newAction.symbol} placeholder="Auto-filled" /></Form.Group>
              <Form.Group className="col-md-8"><Form.Label>Company</Form.Label><Form.Control aria-label="Company" readOnly value={newAction.company_name} placeholder="Select a company above" /></Form.Group>
              <Form.Group className="col-md-4"><Form.Label>Source ISIN</Form.Label><Form.Control aria-label="Source ISIN" readOnly value={newAction.source_isin} placeholder="Auto-filled" /></Form.Group>
              <Form.Group className="col-md-8"><Form.Label>Destination ISIN (when applicable)</Form.Label><Form.Control value={newAction.destination_isin} onChange={(event) => setNewAction({ ...newAction, destination_isin: event.target.value })} /></Form.Group>
              <Form.Group className="col-md-4"><Form.Label>Effective date</Form.Label><Form.Control type="date" required value={newAction.effective_date} onChange={(event) => setNewAction({ ...newAction, effective_date: event.target.value })} /></Form.Group>
              <Form.Group className="col-md-4"><Form.Label>Ex-date</Form.Label><Form.Control type="date" value={newAction.ex_date} onChange={(event) => setNewAction({ ...newAction, ex_date: event.target.value })} /></Form.Group>
              <Form.Group className="col-md-2"><Form.Label>New / bonus</Form.Label><Form.Control type="number" min="0" step="0.000001" value={newAction.ratio_numerator} onChange={(event) => setNewAction({ ...newAction, ratio_numerator: event.target.value })} /></Form.Group>
              <Form.Group className="col-md-2"><Form.Label>Old / held</Form.Label><Form.Control type="number" min="0.000001" step="0.000001" value={newAction.ratio_denominator} onChange={(event) => setNewAction({ ...newAction, ratio_denominator: event.target.value })} /></Form.Group>
              <Form.Group className="col-md-4"><Form.Label>Confidence</Form.Label><Form.Select value={newAction.confidence} onChange={(event) => setNewAction({ ...newAction, confidence: event.target.value })}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="verified">Verified</option></Form.Select></Form.Group>
              <Form.Group className="col-md-8"><Form.Label>Official reference</Form.Label><Form.Control required value={newAction.provider_reference} onChange={(event) => setNewAction({ ...newAction, provider_reference: event.target.value })} /></Form.Group>
              <Form.Group className="col-12"><Form.Label>Review notes</Form.Label><Form.Control as="textarea" rows={3} value={newAction.notes} onChange={(event) => setNewAction({ ...newAction, notes: event.target.value })} /></Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button><Button type="submit" disabled={saving || !selectedStock}>{saving ? <Spinner animation="border" size="sm" /> : 'Create candidate'}</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showEvidence} onHide={() => setShowEvidence(false)} centered>
        <Form onSubmit={addEvidence}>
          <Modal.Header closeButton><Modal.Title>Add evidence</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label>Source name</Form.Label><Form.Control required value={newEvidence.source_name} onChange={(event) => setNewEvidence({ ...newEvidence, source_name: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Reference</Form.Label><Form.Control value={newEvidence.source_reference} onChange={(event) => setNewEvidence({ ...newEvidence, source_reference: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Source URL</Form.Label><Form.Control type="url" value={newEvidence.source_url} onChange={(event) => setNewEvidence({ ...newEvidence, source_url: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Notes</Form.Label><Form.Control as="textarea" rows={3} value={newEvidence.notes} onChange={(event) => setNewEvidence({ ...newEvidence, notes: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Evidence file (optional)</Form.Label><Form.Control type="file" accept=".pdf,.csv,.xls,.xlsx,.png,.jpg,.jpeg" onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewEvidenceFile(event.target.files?.[0] || null)} /><Form.Text>PDF, CSV, Excel, PNG, or JPEG up to 10 MB.</Form.Text></Form.Group>
            <Form.Check type="switch" label="Authoritative official source" checked={newEvidence.is_authoritative} onChange={(event) => setNewEvidence({ ...newEvidence, is_authoritative: event.target.checked })} />
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowEvidence(false)}>Cancel</Button><Button type="submit" disabled={saving}>Add evidence</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(protectedKind)} onHide={() => { setProtectedKind(null); setRollbackPreview(null); setResolutionPreview(null); }} centered>
        <Form onSubmit={submitProtected}>
          <Modal.Header closeButton><Modal.Title>{protectedKind === 'approve' ? 'Approve global action' : protectedKind === 'rollback' ? 'Request rollback' : 'Resolve one user conflict'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Alert variant="danger">
              Review the preview again. This operation writes an immutable audit record and may affect portfolio ledgers when mutation is enabled.
            </Alert>
            {protectedKind === 'rollback' && rollbackPreview && (
              <div className="protected-operation-preview mb-3">
                <div className="protected-operation-preview__summary">
                  <strong>{rollbackPreview.safe_count} safe to reverse</strong>
                  <span>{rollbackPreview.blocked_count} dependency conflict(s)</span>
                </div>
                {rollbackPreview.rows.map((row) => (
                  <div key={row.application_id} className={row.safe ? 'is-safe' : 'is-blocked'}>
                    <span>{row.username} / {row.broker_account_name}</span>
                    <strong>{row.safe ? 'Safe' : row.blockers.join('; ')}</strong>
                  </div>
                ))}
              </div>
            )}
            {protectedKind === 'resolve' && (
              <>
                <div className="row g-2 mb-3">
                  <Form.Group className="col-6"><Form.Label>Target source quantity</Form.Label><Form.Control type="number" min="0" step="0.000001" required value={targetSource} onChange={(event) => { setTargetSource(event.target.value); setResolutionPreview(null); setConfirmation(''); }} /></Form.Group>
                  <Form.Group className="col-6"><Form.Label>Target destination quantity</Form.Label><Form.Control type="number" min="0" step="0.000001" value={targetDestination} onChange={(event) => { setTargetDestination(event.target.value); setResolutionPreview(null); setConfirmation(''); }} /></Form.Group>
                </div>
                <Button type="button" variant="outline-dark" className="mb-3" onClick={generateResolutionPreview} disabled={saving || !targetSource}>Preview this correction</Button>
                {resolutionPreview && (
                  <div className="protected-operation-preview mb-3">
                    <div><span>Source</span><strong>{resolutionPreview.source_current} to {resolutionPreview.source_target} ({resolutionPreview.source_delta})</strong></div>
                    <div><span>Destination</span><strong>{resolutionPreview.destination_current} to {resolutionPreview.destination_target} ({resolutionPreview.destination_delta})</strong></div>
                  </div>
                )}
              </>
            )}
            <Form.Group className="mb-3"><Form.Label>Reason</Form.Label><Form.Control as="textarea" rows={3} required value={reason} onChange={(event) => setReason(event.target.value)} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Current password</Form.Label><Form.Control type="password" required value={password} onChange={(event) => setPassword(event.target.value)} /></Form.Group>
            <Form.Group><Form.Label>Type exactly: <code>{requiredPhrase}</code></Form.Label><Form.Control required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => { setProtectedKind(null); setRollbackPreview(null); setResolutionPreview(null); }}>Cancel</Button><Button type="submit" variant="danger" disabled={saving || !requiredPhrase || confirmation !== requiredPhrase || reason.trim().length < 3}>{saving ? <Spinner animation="border" size="sm" /> : 'Confirm operation'}</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SuperAdminCorporateActions;
